/**
 * LoginPage — FR-01
 * 経験則 #15: 非同期中はボタン disabled / サーバエラーは _general に集約
 */

import { useState, useContext, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { loginUser } from '../api'
import { ApiClientError } from '../api/client'
import { AuthContext } from '../contexts/AuthContext'
import Spinner from '../components/Spinner'

export default function LoginPage() {
  const { setUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const user = await loginUser({ email, password })
      setUser(user)
      if (!user?.current_cert_type) {
        navigate('/cert-select', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError('ログインに失敗しました。しばらく経ってから再試行してください。')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-base px-6 py-10">
      <div className="w-full max-w-[400px] rounded-lg border border-border bg-surface p-8 shadow-card">
        <h1 className="mb-1 text-center text-[22px] font-bold tracking-tight text-text-primary">
          衛生管理者 試験対策
        </h1>
        <p className="mb-7 text-center text-sm text-text-secondary">合格まで、確かな一歩ずつ</p>

        {/* サーバーエラー (_general 領域 #15) */}
        {error && (
          <div data-testid="login-error" role="alert" className="alert-error mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="field-label">
              メールアドレス
            </label>
            <input
              id="login-email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="login-password" className="field-label">
              パスワード
            </label>
            <div className="relative">
              <input
                id="login-password"
                data-testid="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pr-12"
              />
              <button
                type="button"
                aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center rounded-sm p-1 text-text-muted transition-colors hover:text-text-secondary"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {showPassword ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                      <path d="M1 1l22 22" />
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <button type="submit" data-testid="login-submit" disabled={loading} className="btn-primary w-full">
            {loading ? <Spinner label="ログイン中..." /> : 'ログイン'}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-text-secondary">
          アカウントをお持ちでない方{' '}
          <Link to="/register" data-testid="link-to-register" className="font-semibold text-accent hover:underline">
            新規登録
          </Link>
        </p>
      </div>
    </div>
  )
}
